import { createRoutine } from 'redux-saga-routines'
import { call, cancelled, fork, put, select, takeEvery, takeLatest } from '@redux-saga/core/effects'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { uploadFileToS3 } from 'services/fileService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import { getCurrentUser } from 'features/auth/ducks/selectors'

export const loadEstateFilesByFolderRoutine = createRoutine('LOAD_ESTATE_FILES_BY_FOLDER')
export const loadEstateFilesByCategoryRoutine = createRoutine('LOAD_ESTATE_FILES_BY_CATEGORY')
export const loadEstateFilesByBpRoutine = createRoutine('LOAD_ESTATE_FILES_BY_BP')
export const loadEstateOwnedFilesRoutine = createRoutine('LOAD_ESTATE_OWNED_FILES')
export const loadEstateFoldersRoutine = createRoutine('LOAD_ESTATE_FOLDERS')
export const uploadEstateDocumentRoutine = createRoutine('UPLOAD_ESTATE_DOCUMENT')
export const searchEstateFilesRoutine = createRoutine('SEARCH_ESTATE_FILES')
export const updateEstateLoadingProgressRoutine = createRoutine('UPDATE_ESTATE_LOADING_PROGRESS')
export const getEstateFolderUserCountsRoutine = createRoutine('GET_ESTATE_FOLDER_USER_COUNTS')
export const toggleEstateFolderAccessRoutine = createRoutine('TOGGLE_ESTATE_FOLDER_ACCESS')
export const resetEstateDocumentsRoutine = createRoutine('RESET_ESTATE_DOCUMENTS')

let activeEstateDocumentsController = null
const activeEstateBpControllers = new Map()

function abortActiveEstateDocumentsLoad() {
  if (activeEstateDocumentsController) {
    activeEstateDocumentsController.abort()
    activeEstateDocumentsController = null
  }
}

function* loadEstateFilesByFolder({ payload }) {
  const { estateId, folderId, fileCategories, cursor = null, buildingPassportId, search, fileTypeIds, uploaderIds, tagIds } = payload
  abortActiveEstateDocumentsLoad()
  const abortController = new AbortController()
  activeEstateDocumentsController = abortController
  yield put(loadEstateFilesByFolderRoutine.request({ folderId }))
  try {
    const categoryIds = (fileCategories || [])
      .filter(cat => cat.folderId === folderId)
      .map(cat => cat.id)

    if (categoryIds.length === 0) {
      yield put(loadEstateFilesByFolderRoutine.success({ folderId, files: [], nextCursor: null, hasMore: false, replace: true }))
      return
    }

    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''
    categoryIds.forEach((catId, index) => {
      queryParams[`filter[category][${index}]`] = catId
    })
    if (search) queryParams['filter[search]'] = search
    if (fileTypeIds?.length) queryParams['filter[file_type_ids]'] = fileTypeIds.join(',')
    if (uploaderIds?.length) queryParams['filter[uploader_ids]'] = uploaderIds.join(',')
    if (tagIds?.length) queryParams['filter[tag_ids]'] = tagIds.join(',')
    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) queryParams['filter[hide_duplicates]'] = 1

    const { data } = yield call(estateDocumentsService.getEstateFiles, {
      estateId,
      buildingPassportId,
      query: queryParams
    }, abortController.signal)

    yield put(loadEstateFilesByFolderRoutine.success({
      folderId,
      files: data?.data || [],
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor,
    }))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadEstateFilesByFolderRoutine.failure({ folderId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadEstateFilesByFolderRoutine.failure({ folderId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
    if (activeEstateDocumentsController === abortController) {
      activeEstateDocumentsController = null
    }
  }
}

function* loadEstateFilesByCategory({ payload }) {
  const { estateId, categoryId, cursor = null, buildingPassportId, search, fileTypeIds, uploaderIds, tagIds } = payload
  abortActiveEstateDocumentsLoad()
  const abortController = new AbortController()
  activeEstateDocumentsController = abortController
  yield put(loadEstateFilesByCategoryRoutine.request({ categoryId }))
  try {
    const queryParams = { limit: 50 }
    queryParams.cursor = cursor ?? ''
    if (categoryId === 'uncategorised') {
      queryParams['filter[uncategorised]'] = 'true'
    } else if (categoryId !== 'all_files') {
      queryParams['filter[category]'] = categoryId
    }
    if (search) queryParams['filter[search]'] = search
    if (fileTypeIds?.length) queryParams['filter[file_type_ids]'] = fileTypeIds.join(',')
    if (uploaderIds?.length) queryParams['filter[uploader_ids]'] = uploaderIds.join(',')
    if (tagIds?.length) queryParams['filter[tag_ids]'] = tagIds.join(',')
    const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
    if (hideDuplicates) queryParams['filter[hide_duplicates]'] = 1

    const { data } = yield call(estateDocumentsService.getEstateFiles, {
      estateId,
      buildingPassportId,
      query: queryParams
    }, abortController.signal)

    yield put(loadEstateFilesByCategoryRoutine.success({
      categoryId,
      files: data?.data || [],
      nextCursor: data?.meta?.nextCursor ?? null,
      hasMore: !!data?.meta?.hasMore,
      replace: !cursor,
    }))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadEstateFilesByCategoryRoutine.failure({ categoryId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadEstateFilesByCategoryRoutine.failure({ categoryId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
    if (activeEstateDocumentsController === abortController) {
      activeEstateDocumentsController = null
    }
  }
}

function* loadEstateFilesByBp({ payload }) {
  const { estateId, bpId } = payload
  const key = `bp:${bpId}`
  if (activeEstateBpControllers.has(bpId)) {
    activeEstateBpControllers.get(bpId).abort()
  }
  const abortController = new AbortController()
  activeEstateBpControllers.set(bpId, abortController)
  yield put(loadEstateFilesByBpRoutine.request({ bpId }))
  try {
    let allFiles = []
    let page = 1
    let hasMoreFiles = true

    while (hasMoreFiles) {
      const queryParams = { page, limit: 3000 }

      const hideDuplicates = (yield select(getCurrentUser))?.hideDuplicateFiles ?? true
      if (hideDuplicates) {
        queryParams['filter[hide_duplicates]'] = 1
      }

      const { data } = yield call(estateDocumentsService.getEstateFiles, {
        estateId,
        buildingPassportId: bpId,
        query: queryParams
      }, abortController.signal)

      const filesForPage = data?.data || []
      allFiles = [...allFiles, ...filesForPage]

      yield put(updateEstateLoadingProgressRoutine.success({ key, loaded: allFiles.length }))

      hasMoreFiles = filesForPage.length === 3000

      const uniqueFiles = Array.from(new Map(allFiles.map(file => [file.id, file])).values())
      yield put(loadEstateFilesByBpRoutine.success({ bpId, files: uniqueFiles, partial: hasMoreFiles }))

      page++
    }
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(loadEstateFilesByBpRoutine.failure({ bpId, error }))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadEstateFilesByBpRoutine.failure({ bpId, error }))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
      yield put(loadEstateFilesByBpRoutine.failure({ bpId }))
    }
    if (activeEstateBpControllers.get(bpId) === abortController) {
      activeEstateBpControllers.delete(bpId)
    }
  }
}

function* loadEstateFolders({ payload }) {
  const { estateId } = payload
  yield put(loadEstateFoldersRoutine.request())
  try {
    const { data } = yield call(estateDocumentsService.getEstateFolders, { estateId })
    yield put(loadEstateFoldersRoutine.success({ folders: data?.data || [] }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadEstateFoldersRoutine.failure(error))
  }
}

const ESTATE_OWNED_PAGE_SIZE = 30

export function* loadEstateOwnedFiles({ payload }) {
  const { estateId, page = 1, folderId, reloadLoaded = false } = payload
  const normalizedFolderId = folderId || 'all'
  let { limit = ESTATE_OWNED_PAGE_SIZE } = payload
  let pagesCovered = null
  if (reloadLoaded) {
    const owned = yield select(state => state.estateDocuments || {})
    const sameFolder = String(owned.estateOwnedFilesFolderId || 'all') === String(normalizedFolderId)
    pagesCovered = sameFolder ? Math.max(1, owned.estateOwnedFilesMeta?.currentPage || 1) : 1
    limit = pagesCovered * ESTATE_OWNED_PAGE_SIZE
  }
  yield put(loadEstateOwnedFilesRoutine.request())
  try {
    const effectiveFolderId = normalizedFolderId !== 'all' ? normalizedFolderId : undefined
    const { data } = yield call(estateDocumentsService.getEstateOwnedFiles, {
      estateId,
      page,
      limit,
      folderId: effectiveFolderId
    })
    const files = data?.data || []
    const rawMeta = data?.meta || {}
    const meta = pagesCovered
      ? { ...rawMeta, currentPage: pagesCovered, lastPage: Math.max(1, Math.ceil((rawMeta.total ?? files.length) / ESTATE_OWNED_PAGE_SIZE)) }
      : rawMeta
    yield put(loadEstateOwnedFilesRoutine.success({ files, page, meta, folderId: normalizedFolderId }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(loadEstateOwnedFilesRoutine.failure(error))
  }
}

export function* uploadEstateDocument({ payload }) {
  const { estateId, file, folderId } = payload
  const displayName = file.name
  yield put(uploadEstateDocumentRoutine.request({ estateId, displayName }))
  try {
    const { data: presigned } = yield call(estateDocumentsService.generateEstateUploadUrl, {
      estateId,
      displayName
    })
    const { presignedUrl, s3Key } = presigned.data

    yield call(uploadFileToS3, presignedUrl, file)

    yield call(estateDocumentsService.uploadEstateFile, {
      estateId,
      files: [{
        path: s3Key,
        originalFileName: file.name,
        displayName,
        size: file.size,
        mimeType: file.type
      }]
    })

    yield put(uploadEstateDocumentRoutine.success({ estateId, displayName }))
    toast.success(`${displayName} uploaded.`)
    const viewingFolderId = yield select(state => state.estateDocuments?.estateOwnedFilesRequestedFolderId ?? null)
    if (String(viewingFolderId || 'all') === String(folderId || 'all')) {
      yield put(loadEstateOwnedFilesRoutine({ estateId, page: 1, folderId }))
    }
    yield put(loadEstateFoldersRoutine({ estateId }))
  } catch (error) {
    yield put(uploadEstateDocumentRoutine.failure({ estateId, displayName, error }))
    const apiError = getApiErrors(error)
    toast.error(apiError?.trim() ? apiError : `Could not upload ${displayName}.`)
  }
}

function* searchEstateFiles({ payload }) {
  const { estateId, query, uploaderIds, fileTypeIds, fileCategoryIds, fileTagIds } = payload
  const abortController = new AbortController()
  yield put(searchEstateFilesRoutine.request())
  try {
    const { data } = yield call(estateDocumentsService.searchEstateFiles, {
      estateId,
      query,
      uploaderIds,
      fileTypeIds,
      fileCategoryIds,
      fileTagIds
    }, abortController.signal)

    yield put(searchEstateFilesRoutine.success({
      results: data?.data || [],
      meta: data?.meta || {}
    }))
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      yield put(searchEstateFilesRoutine.failure(error))
      return
    }
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(searchEstateFilesRoutine.failure(error))
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    }
  }
}

function* getEstateFolderUserCounts({ payload }) {
  const { estateId } = payload
  yield put(getEstateFolderUserCountsRoutine.request())
  try {
    const { data } = yield call(estateDocumentsService.getEstateFolderUserCounts, { estateId })
    yield put(getEstateFolderUserCountsRoutine.success(data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getEstateFolderUserCountsRoutine.failure(error))
  }
}

function* toggleEstateFolderAccess({ payload }) {
  const { estateId, userId, folders } = payload
  yield put(toggleEstateFolderAccessRoutine.request())
  try {
    yield call(estateDocumentsService.toggleEstateFolderAccess, { estateId, userId, folders })
    yield put(toggleEstateFolderAccessRoutine.success())
    yield put(getEstateFolderUserCountsRoutine.trigger({ estateId }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(toggleEstateFolderAccessRoutine.failure(error))
  }
}

export function* loadEstateFilesByFolderWatcher() {
  yield takeLatest(loadEstateFilesByFolderRoutine.TRIGGER, loadEstateFilesByFolder)
}

export function* loadEstateFilesByCategoryWatcher() {
  yield takeLatest(loadEstateFilesByCategoryRoutine.TRIGGER, loadEstateFilesByCategory)
}

export function* loadEstateFilesByBpWatcher() {
  yield takeEvery(loadEstateFilesByBpRoutine.TRIGGER, loadEstateFilesByBp)
}

export function* loadEstateFoldersWatcher() {
  yield takeLatest(loadEstateFoldersRoutine.TRIGGER, loadEstateFolders)
}

export function* loadEstateOwnedFilesWatcher() {
  yield takeLatest(loadEstateOwnedFilesRoutine.TRIGGER, loadEstateOwnedFiles)
}

export function* uploadEstateDocumentWatcher() {
  yield takeEvery(uploadEstateDocumentRoutine.TRIGGER, uploadEstateDocument)
}

export function* searchEstateFilesWatcher() {
  yield takeLatest(searchEstateFilesRoutine.TRIGGER, searchEstateFiles)
}

export function* getEstateFolderUserCountsWatcher() {
  yield takeLatest(getEstateFolderUserCountsRoutine.TRIGGER, getEstateFolderUserCounts)
}

export function* toggleEstateFolderAccessWatcher() {
  yield takeEvery(toggleEstateFolderAccessRoutine.TRIGGER, toggleEstateFolderAccess)
}

export const estateDocumentsSagas = [
  fork(loadEstateFilesByFolderWatcher),
  fork(loadEstateFilesByCategoryWatcher),
  fork(loadEstateFilesByBpWatcher),
  fork(loadEstateFoldersWatcher),
  fork(loadEstateOwnedFilesWatcher),
  fork(uploadEstateDocumentWatcher),
  fork(searchEstateFilesWatcher),
  fork(getEstateFolderUserCountsWatcher),
  fork(toggleEstateFolderAccessWatcher)
]
